<template>
	<v-container fluid>
		<v-card width="100%" class="overflow-scroll my-4">
			<v-toolbar
				flat
				color="white"
				class="toolbar mb-1"
			>
				<v-text-field
					v-model="search"
					append-icon="search"
					label="Search"
					class="mr-5"
					single-line
					hide-details
				/>
				<v-menu offset-y>
					<template slot="activator" slot-scope="{ on }">
						<v-btn
							flat
							class="mt-3"
							color="primary"
							v-on="on"
							:disabled="accounts.changing || selected.length === 0"
						>
							With Selected
						</v-btn>
					</template>
					<v-list>
						<v-list-tile @click="hideSelected()">
							<v-list-tile-title>Hide</v-list-tile-title>
						</v-list-tile>
					</v-list>							
				</v-menu>
				<v-tooltip top>
      				<template slot="activator" slot-scope="{ on }">
						<v-btn
							flat
							class="mt-3"
							color="primary"
							v-on="on"
							:disabled="accounts.changing || accounts.hiddenModels.length === 0"
							@click="showHidden = !showHidden"
						>
							{{ showHidden ? 'Hide Hidden' : 'Show Hidden' }}
						</v-btn>
      				</template>
					<span>Hidden: {{ accounts.hiddenModels.length }}</span>
				</v-tooltip>
				<v-tooltip top>
					<template slot="activator" slot-scope="{ on }">
						<v-btn
							flat
							icon
							class="mt-3 mr-4"
							color="primary"
							:disabled="accounts.changing"
							v-on="on"
							@click="fetch"
						>
							<v-icon light>refresh</v-icon>
						</v-btn>
					</template>
					<span>Refresh</span>
				</v-tooltip>
			</v-toolbar>
			<v-divider />
			<v-data-table
				v-if="accounts"
				v-model="selected"
				:headers="headers"
				:items="accountsInTable"
				:loading="accounts.changing"
				:search="search"
				:rows-per-page-items="rowsPerPageItems"
				no-data-text=""
				class="accounts-table"
			>
				<template slot="items" slot-scope="props">
					<tr
						v-if="!props.item.hidden || showHidden"
						:class="{ 'hidden-account': props.item.hidden }"
					>
						<td>
							<v-checkbox
								v-model="props.selected"
								color="primary"
								hide-details
								:disabled="props.item.changing || props.item.hidden"
							/>
						</td>
						<td>{{ props.item.id }}</td>
						<td>{{ props.item.name }}</td>
						<td class="text-xs-center">
							{{ props.item.stats.activeProjects | formatNumber }}
						</td>
						<td class="text-xs-center">
							{{ props.item.stats.archivedProjects | formatNumber }}
						</td>
						<td class="text-xs-center">
							{{ props.item.stats.activeReports | formatNumber }}
						</td>
						<td class="text-xs-center">
							{{ props.item.stats.archivedReports | formatNumber }}
						</td>
						<td class="text-xs-center">
							{{ props.item.stats.articlesLastThirtyDays | formatNumber }}
						</td>
						<td>
							<span class="card-cell" v-if="props.item.card">
								<v-icon class="cc-icon">credit_card_outline</v-icon>
								<span>{{ props.item.card.brand }}</span>
								<span>*{{ props.item.card.last4 }}</span>
								<span>{{ props.item.card.exp_month }} / {{ props.item.card.exp_year }}</span>
							</span>
							<span v-else>
								No card
							</span>
						</td>
						<td>
							<v-menu offset-y left>
								<template slot="activator" slot-scope="{ on }">
									<v-btn
										small
										icon
										v-on="on"
										:disabled="props.item.saving"
										:loading="props.item.saving"
									>
										<v-icon>more_vert</v-icon>
									</v-btn>
								</template>
								<v-list>
									<v-list-tile @click="openUsersDialog(props.item)">
										<v-list-tile-title>Users</v-list-tile-title>
									</v-list-tile>
									<v-list-tile @click="changeHidden(props.item, !props.item.hidden)">
										<v-list-tile-title>{{ props.item.hidden ? 'Unhide' : 'Hide' }}</v-list-tile-title>
									</v-list-tile>
								</v-list>
							</v-menu>
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card>
		<Dialog
			v-if="selectedAccount"
			:title="`Users for account ${selectedAccount.name}`"
			max-width="90vw"
			:show="showUsersDialog"
			:loading="selectedAccount.changing"
			@close="showUsersDialog = false"
		>
			<template slot="content">
				<template v-if="selectedAccount.users.length > 0">
					<v-data-table
						:headers="headersUsersTable"
						:items="selectedAccount.users"
						:loading="false"
						class="users-table"
						hide-actions
					>
						<template slot="items" slot-scope="{ item }">
							<tr>
								<td>{{ item.id }}</td>
								<td>{{ item.email }}</td>
								<td>{{ item.realName }}</td>
								<td class="users-table-select-roles">
									<v-autocomplete
										v-if="settableRoles && settableRoles.length > 0"
										v-model="item.settableRoles"
										:disabled="item.saving"
										:items="settableRoles"
										menu-props="closeOnContentClick"
										chips
										deletable-chips
										outline
										hide-selected
										hide-details
										flat
										multiple
									/>
									<p v-else class="error--text text-xs-center mb-0">Roles not loaded</p>
								</td>
								<td>
									<v-btn
										flat
										color="success"
										:disabled="!(settableRoles && settableRoles.length > 0) || item.saving"
										:loading="item.saving"
										@click="saveRoles(item)"
									>
										Save
									</v-btn>
								</td>
							</tr>
						</template>
					</v-data-table>
				</template>
				<template v-else>
					No users found.
				</template>
			</template>
		</Dialog>
	</v-container>
</template>

<script>
import Dialog from '@/components/Global/Dialog';
import { sync } from 'vuex-pathify';
import { Accounts } from '@/models/Account';
import axios from '@/util/axios';

export default {
	components: {
		Dialog,
	},
	data() {
		return {
			// Accounts table
			headers: [
				{ text: '', sortable: false },
				{ text: 'ID', value: 'id' },
				{ text: 'Name', value: 'name', class: 'accounts-table-header-name' },
				{ text: 'Active Projects', value: 'stats.activeProjects', class: 'accounts-table-header-stats' },
				{ text: 'Archived Projects', value: 'stats.archivedProjects', class: 'accounts-table-header-stats' },
				{ text: 'Active Reports', value: 'stats.activeReports', class: 'accounts-table-header-stats' },
				{ text: 'Archived Reports', value: 'stats.archivedReports', class: 'accounts-table-header-stats' },
				{ text: 'Articles Last 30 Days', value: 'stats.articlesLastThirtyDays', class: 'accounts-table-header-stats' },
				{ text: 'Card', value: 'card', sortable: false },
				{ text: 'Action', sortable: false }
			],
			rowsPerPageItems: [
				25,
				50,
				100,
				{ text: 'All', value: -1 },
			],
			search: '',
			selected: [],
			accountsInTable: [],
			showHidden: false,
			// Account dialog
			showUsersDialog: false,
			selectedAccount: null,
			loadingUsers: true,
			// Users table
			headersUsersTable: [
				{ text: 'ID', value: 'id' },
				{ text: 'Email (login)', value: 'email' },
				{ text: 'Name', value: 'realName' },
				{ text: 'Roles', sortable: false, class: 'users-table-select-roles-header' },
				{ text: 'Action', sortable: false },
			],
			settableRoles: [],
		};
	},
	created() {
		this.loadSettableRoles();
		if (this.accounts === null) {
			this.fetch();
		}
	},
	computed: {
		accounts: sync('accounts'),
	},
	watch: {
		showHidden(showHidden) {
			this.accountsInTable = showHidden ? this.accounts.models : this.accounts.visibleModels;
		},
	},
	methods: {
		loadSettableRoles() {
			axios.get('/super/roles').then((resp) => {
				this.settableRoles = resp.data;
			}).catch((e) => {
				dump('Error getting settable roles', e);
				this.$notify('error', 'Failed to fetch roles.');
			});
		},
		fetch() {
			this.accountsInTable = [];
			this.accounts = new Accounts();
			this.accounts.fetch().then(() => {
				this.accountsInTable = this.showHidden ? this.accounts.models : this.accounts.visibleModels;
			}).catch((e) => {
				dump('Error fetching accounts', e);
				this.$notify('error', 'Failed to get accounts, refresh to try again.');
			});
		},
		changeHidden(account, hide) {
			const un = hide ? '' : 'un';
			account.hidden = hide;
			account.save().then(() => {
				this.$notify('success', `Account "${account.name}" has been ${un}hidden.`);
			}).catch((e) => {
				dump(`Error ${un}hiding account`, e);
				this.$notify('error', `Failed to ${un}hide account.`);
			});
		},
		hideSelected() {
			const accounts = new Accounts(this.selected);
			this.accounts.saving = true;
			accounts.hide().then(() => {
				this.selected = [];
				this.$notify('success', 'Selected accounts have been hidden.');
			}).catch((e) => {
				dump('Error hiding account', e);
				this.$notify('error', 'Failed to hide some or all selected accounts.');
			}).finally(() => {
				this.accounts.saving = false;
			});
		},
		openUsersDialog(account) {
			this.showUsersDialog = true;
			this.selectedAccount = account;
			this.selectedAccount.fetchUsers().then().catch((e) => {
				dump('Error fetching users for account', e);
				this.$notify('error', 'Failed to fetch users.');
			});
		},
		saveRoles(user) {
			const data = {
				id: user.id,
				roles: user.settableRoles,
			};
			user.saving = true;
			axios.post('/super/update-user-roles', data).then((resp) => {
				this.$notify('success', `Roles for user ${user.name} have been saved.`);
			}).catch((e) => {
				dump('Error saving user\'s roles', e);
				this.$notify('error', `Failed to save roles for user ${user.name}.`);
			}).finally(() => {
				user.saving = false;
			});
		},
	}
};
</script>

<style lang="scss">
.toolbar {
	.v-toolbar__content {
		padding-right: 0;
	}
}
.card-cell {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	> span {
		margin-left: 10px;
	}
	.cc-icon {
		display: inline-block;
		width: 20px;
	}
}
.v-input--selection-controls__input {
	margin: 0;
}
.accounts-table {
	table.v-table {
		th,
		td {
			padding: 0 10px !important;
		}
		thead {
			th {
				&.accounts-table-header-name {
					min-width: 200px;
					max-width: 200px;
				}
			}
		}
		tr.hidden-account {
			background-color: #eee;
			opacity: 0.5;
		}
	}
}
.v-dialog {
	.v-card__text {
		padding: 0;
	}
	.users-table {
		table.v-table {
			tr:hover {
				background-color: transparent;
			}
			th:first-child,
			td:first-child,
			th:last-child {
				text-align: center !important;
			}
			th.users-table-select-roles-header,
			td.users-table-select-roles {
				padding: 0;
				width: 50%;
			}
			th.users-table-select-roles-header {
				text-align: center !important;
			}
		}
		.v-input__slot {
			border-width: 1px !important;
			border-style: dashed !important;
			border-radius: 0 !important;
			.v-select__selections {
				padding-top: 0;
				min-height: 42px !important;
			}
		}
	}
}
</style>
